// Compatibility Feed Item Component
// Displays compatibility suggestions with swipe mode integration

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
  SwipeModeToggle,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { CompatibilityItemProps, FeedInteraction } from "../../../types/socialFeed";

export const CompatibilityItem: React.FC<CompatibilityItemProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = true,
  isFullScreen = false,
  aspectRatio,
}) => {
  const { content, engagement } = item;

  // Get compatibility score color
  const getCompatibilityColor = (score: number): string => {
    if (score >= 90) return "#2ecc71"; // Green
    if (score >= 80) return "#f39c12"; // Orange
    if (score >= 70) return "#e74c3c"; // Red
    return "#95a5a6"; // Gray
  };

  // Get compatibility level
  const getCompatibilityLevel = (score: number): string => {
    if (score >= 90) return "Perfect Match";
    if (score >= 80) return "Great Match";
    if (score >= 70) return "Good Match";
    return "Potential Match";
  };

  return (
    <FeedItemContent>
      {/* Background with compatibility gradient */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${getCompatibilityColor(content.compatibilityScore)}20 0%, ${getCompatibilityColor(content.compatibilityScore)}40 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Compatibility Score Circle */}
        <motion.div
          style={{
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${getCompatibilityColor(content.compatibilityScore)} 0%, ${getCompatibilityColor(content.compatibilityScore)}80 100%)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "1.5rem",
            fontWeight: "700",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.2,
            type: "spring",
            stiffness: 200 
          }}
        >
          <span style={{ fontSize: "2rem" }}>💕</span>
          <span style={{ fontSize: "1.2rem", marginTop: "0.25rem" }}>
            {content.compatibilityScore}%
          </span>
        </motion.div>
      </motion.div>

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          <ContentTitle style={{ color: getCompatibilityColor(content.compatibilityScore) }}>
            {getCompatibilityLevel(content.compatibilityScore)}
          </ContentTitle>
          <ContentDescription>
            {content.matchReasons.slice(0, 2).join(" • ")}
          </ContentDescription>
          
          {/* Shared Interests */}
          {content.sharedInterests.length > 0 && (
            <div style={{ 
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              fontSize: "0.8rem",
            }}>
              <strong>Shared Interests:</strong> {content.sharedInterests.slice(0, 3).join(", ")}
            </div>
          )}

          {/* Conversation Starters */}
          {content.conversationStarters.length > 0 && (
            <div style={{
              marginTop: "0.5rem",
              padding: "0.5rem",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "8px",
              fontSize: "0.8rem",
            }}>
              <strong>Conversation Starter:</strong> "{content.conversationStarters[0]}"
            </div>
          )}

          {/* Personality Alignment */}
          {content.personalityAlignment && (
            <div style={{
              marginTop: "0.5rem",
              display: "flex",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}>
              <span style={{
                padding: "0.25rem 0.5rem",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                fontSize: "0.7rem",
                fontWeight: "500",
              }}>
                MBTI: {content.personalityAlignment.mbtiCompatibility}%
              </span>
              <span style={{
                padding: "0.25rem 0.5rem",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                fontSize: "0.7rem",
                fontWeight: "500",
              }}>
                Communication: {content.personalityAlignment.communicationStyle}%
              </span>
            </div>
          )}
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{engagement.shares}</ActionCount>

        {/* View Profile Button */}
        <ActionButton
          onClick={() => onUserClick(item.userId)}
        >
          👤
        </ActionButton>
      </ActionButtons>

      {/* Swipe Mode Toggle - Always shown for compatibility items */}
      {onSwipeModeTrigger && (
        <SwipeModeToggle
          $show={true}
          onClick={() => onSwipeModeTrigger(item)}
        >
          💕 Swipe to Connect
        </SwipeModeToggle>
      )}
    </FeedItemContent>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
